import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { apiFetch } from '../hooks/useFetch';

export interface AuthUser {
  id: string;
  email: string;
  papel: 'super_admin' | 'partner_admin';
  nome: string;
  company_id: string | null;
}

export interface Impersonate {
  company_id: string;
  nome: string;
}

interface AuthState {
  loading: boolean;
  user: AuthUser | null;
  impersonate: Impersonate | null;
  csrfToken: string | null;
}

interface AuthCtxValue extends AuthState {
  login: (email: string, password: string) => Promise<{ ok: boolean; message?: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setImpersonate: (companyId: string | null) => Promise<void>;
  apiFetch: <T = unknown>(url: string, opts?: Parameters<typeof apiFetch>[1]) => ReturnType<typeof apiFetch<T>>;
}

const AuthCtx = createContext<AuthCtxValue | null>(null);

export function useAuth(): AuthCtxValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth fora de AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    loading: true,
    user: null,
    impersonate: null,
    csrfToken: null
  });

  // Mantém o csrf atual em ref para o refresher do apiFetch (evita stale closure)
  const csrfRef = useRef<string | null>(null);
  csrfRef.current = state.csrfToken;

  const refreshCsrf = useCallback(async (): Promise<string | null> => {
    const r = await fetch('/v1/admin/auth/me', {
      credentials: 'include',
      headers: { Accept: 'application/json' }
    });
    if (!r.ok) return null;
    const d: any = await r.json().catch(() => null);
    const token = d?.csrf_token ?? null;
    if (token) {
      csrfRef.current = token;
      setState((s) => ({
        ...s,
        csrfToken: token,
        user: d.user ?? s.user,
        impersonate: d.impersonate ?? null
      }));
    }
    return token;
  }, []);

  const boundApiFetch = useCallback(
    <T,>(url: string, opts: Parameters<typeof apiFetch>[1] = {}) => {
      return apiFetch<T>(url, opts, csrfRef.current, refreshCsrf);
    },
    [refreshCsrf]
  );

  const refresh = useCallback(async () => {
    const r = await fetch('/v1/admin/auth/me', {
      credentials: 'include',
      headers: { Accept: 'application/json' }
    });
    if (!r.ok) {
      setState({ loading: false, user: null, impersonate: null, csrfToken: null });
      return;
    }
    const d: any = await r.json().catch(() => null);
    setState({
      loading: false,
      user: d?.user ?? null,
      impersonate: d?.impersonate ?? null,
      csrfToken: d?.csrf_token ?? null
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await apiFetch<any>(
        '/v1/admin/auth/login',
        { method: 'POST', body: { email, password } },
        null,
        async () => null
      );
      if (!res.ok || !res.data?.ok) {
        return { ok: false, message: res.error?.message ?? 'Falha ao entrar' };
      }
      setState({
        loading: false,
        user: res.data.user,
        impersonate: null,
        csrfToken: res.data.csrf_token
      });
      return { ok: true };
    },
    []
  );

  const logout = useCallback(async () => {
    await apiFetch(
      '/v1/admin/auth/logout',
      { method: 'POST' },
      csrfRef.current,
      refreshCsrf
    );
    setState({ loading: false, user: null, impersonate: null, csrfToken: null });
  }, [refreshCsrf]);

  const setImpersonate = useCallback(
    async (companyId: string | null) => {
      await apiFetch(
        '/v1/admin/auth/impersonate',
        { method: 'POST', body: { company_id: companyId } },
        csrfRef.current,
        refreshCsrf
      );
      await refresh();
    },
    [refresh, refreshCsrf]
  );

  const value = useMemo<AuthCtxValue>(
    () => ({
      ...state,
      login,
      logout,
      refresh,
      setImpersonate,
      apiFetch: boundApiFetch
    }),
    [state, login, logout, refresh, setImpersonate, boundApiFetch]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
